import { Box, Divider, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { lazy, Suspense, useCallback, useState, useMemo, useEffect } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useGeneralFields, useTriggerPoiEditing } from '../../hooks';
import { GeneralFieldsForm } from '../GeneralFieldsForm';
import { TemplateFieldsForm } from '../TemplateFieldsForm';
import { useTemplateFields } from '../../hooks/useTemplateFields';
import {
  useCreatePointOfInterestMutation,
  PoiStatus,
  VisibilityEnum,
  PoiAccessibilityEnum,
  useAllAssembliesForCreatePoiQuery,
  useGetRecommendationsForAssemblyLazyQuery,
  useUpdatePointOfInterestMutation,
  AllPointOfInterestsForDataLoaderDocument,
  SelectedPointOfInterestDocument,
  GetAssemblyPointOfInterestsDocument,
  useGetAllTeamsForCreateOrEditPoiQuery,
  GetOneAssemblyForBlistersDocument,
  useAllPaintRegionsForFilterAndSearchPanelQuery,
  PointOfInterestsCountByStatusDocument,
  PointOfInterestTemplateTypeEnum,
  DefectsAreaForCustomBlocksDocument,
  ImageType,
  useSavedBlockPoIsLazyQuery,
  SavedBlockPoIsDocument,
  useFindClosestPaintRegionLazyQuery,
  BlisterHeightType,
  UnitSystemEnum,
} from '@/__generated__/graphql';
import { convertTemplateStateToTemplateFieldData } from '../../utils';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { PoiFormHeading, PoiFormSubHeading } from '../styles';
import { BlisterFormFields } from '../BlisterFormFields';
import * as poiState from '@/components/Analysis/modules/pointOfInterest/state';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import { useBlisterData } from '../../hooks/useBlisterData';
import { useMeasurementTool } from '@/components/Analysis/modules/measurementTool';
import { StyledForm } from '../UpdateGeneralFields/styles';
import { SaveCancelFormButtons } from '../SaveCancelFormButtons';
import { saveCancelButtonCommonStyles } from '../../../styles';
import { convertTemplateNameToPoiType, upperSnakeCase, getPoiTemplateKey } from '@/utils';
import { useViewpointPoiHeight } from '@/components/Analysis/Viewer/hooks/useViewpointPoiHeight';
import {
  SceneOptions,
  useSavePoiScreenshot,
} from '@/components/Analysis/modules/pointOfInterest/createOrEdit/useSavePoiScreenshot';
import { paintDiameterSharedState } from '@/components/shared/CalculatedFields/state';
import {
  ADD_POI_LABEL,
  ADD_SCOPE_LABEL,
  BLISTER,
  BLISTER_TYPE,
  CREATING_LABEL,
  PIT,
  PIT_TYPE,
  SAVE_POI_LABEL,
  SAVING_LABEL,
  WORK_SCOPE_LABEL,
} from '@/constants';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import * as cuboidsState from '../../../../../cuboids/state';
import * as customBlocksState from '@/components/Analysis/modules/CustomBlocks/state';
import { measurementsByField } from '@/components/Analysis/modules/measurementTool/state';
import { LinkedPoiEditor } from '../LinkedPoiEditor';
import { useAnalysisCuboidActions } from '@/components/Analysis/modules/cuboids/hooks/useAnalysisCuboidActions';
import { usePoiFlow } from '@/components/Analysis/Insights/InsightsExplorer/PointOfInterests/usePoiFlow';
import { DropDownField } from '@/components/shared/CustomField';
import { PointOfInterestTemplateDocument } from '@/types';
import { propsToPoiLineMeasurement } from '@/components/Analysis/modules/measurementTool/hooks/utils';
import { useUncertainField } from '../../hooks/useUncertainField';
import { useIsPoiWorkScope } from '@/hooks/useIsPoiWorkScope';
import { useFetchWorkscopeDefects } from '../../hooks/useFetchWorkscopeDefects';
import { transformObjectKeys } from '@/utils/objects';
import { useSelectedPaintRegionGroupId } from '@/components/Analysis/hooks/useSelectedPaintRegionGroupId';
import { humanizeString } from '@/utils/string';
import { useRouter } from '@/hooks';
import { isNumeric } from '@/utils/numbers';
import convert from 'convert';

const fieldsContainer = {
  '& > *': { my: '4%' },
};

const BlisterMeasurementLazy = lazy(() =>
  import('../../../../createOrEdit').then(({ BlisterMeasurement }) => ({
    default: BlisterMeasurement,
  }))
);

export const CreatePoi = () => {
  const selectedPaintRegionGroupId = useSelectedPaintRegionGroupId();
  const [editPointOfInterest, setEditPointOfInterest] = useAtom(poiState.editPointOfInterest);
  const allPoiTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const setPaintDiameter = useSetAtom(paintDiameterSharedState);
  const heightToPointOfInterest = useViewpointPoiHeight();
  const { handleSavePoiScreenshot, setSceneOptions } = useSavePoiScreenshot({
    imageType: ImageType.Primary,
  });
  const setAllPOI = useSetAtom(poiState.structurePois);
  const editingCuboids = useAtomValue(cuboidsState.editingCuboids);
  const cuboidLinkedPois = useAtomValue(cuboidsState.cuboidLinkedPois);
  const isCuboidEditorEnabled = useAtomValue(cuboidsState.isCuboidEditorEnabled);
  const setIsAddingWorkscope = useSetAtom(cuboidsState.isAddingWorkscope);
  const setIsCuboidVisibilityBoxEnabled = useSetAtom(cuboidsState.isCuboidVisibilityBoxEnabled);
  const setNominatedBlister = useSetAtom(state.governingValueByAssemblyId);
  const selectedAssembly = useAtomValue(state.selectedAssemblyId);
  const setPoiCounts = useSetAtom(state.poiCountsByType);
  const measurementsMap = useAtomValue(measurementsByField);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const setManualBlisterHeightExists = useSetAtom(poiState.manualBlisterHeightExists);
  const unitSystem = useAtomValue(state.unitSystem);

  const navbarHeight = useNavbarHeight();

  const router = useRouter();

  const { currentTemplate, selectedAssemblyId, setAssemblyId, changePoiTemplate } = usePoiFlow();
  const pointOfInterest = editPointOfInterest?.pointOfInterest;
  const template = editPointOfInterest?.template;
  const isManuallyCreated = editPointOfInterest?.isManuallyCreated;
  const isAutoGenerated = editPointOfInterest?.isAutoGenerated;
  const { cancelEditing } = useTriggerPoiEditing();
  const { stopAddingCuboid, startEditingCurrentCuboid } = useAnalysisCuboidActions();

  const onClose = useCallback(() => {
    setPaintDiameter('0');
    cancelEditing();
    stopAddingCuboid();
  }, [cancelEditing, setPaintDiameter, stopAddingCuboid]);

  const poiType = convertTemplateNameToPoiType(getPoiTemplateKey(template));

  const [getAssemblyRecommendation] = useGetRecommendationsForAssemblyLazyQuery({
    variables: {
      assemblyId: selectedAssemblyId,
    },
    fetchPolicy: 'network-only',
  });

  const { data: teamsData, loading: teamsLoading } = useGetAllTeamsForCreateOrEditPoiQuery();

  const teamOptions = useMemo(() => {
    return teamsData?.allTeams?.map(({ id, name }) => ({
      label: name,
      value: id,
    }));
  }, [teamsData?.allTeams]);
  const setSelectedPointOfInterestId = useSetAtom(poiState.selectedPointOfInterestId);
  const setAllSavedBlocks = useSetAtom(customBlocksState.allSavedBlocks);
  const allTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const setNewPointOfInterestCenterPoint3d = useSetAtom(poiState.newPointOfInterestCenterPoint3d);

  // Lazy query to refetch saved blocks after workscope creation
  const [refetchSavedBlocks] = useSavedBlockPoIsLazyQuery();
  const [loading, setLoading] = useState(false);

  const templateFieldsData = useTemplateFields({
    selectedPoiInfo: pointOfInterest,
    templateFields: template?.fields ?? [],
  });

  const {
    localBlisterData,
    blisterFieldsData,
    pointSelectMessage,
    onMeasure,
    onSelectPoint,
    clearBlisterData,
    handleManualHeightChange,
    handleMeasureIconClick,
    handleChangeControlledField,
    setLocalBlisterData,
  } = useBlisterData(templateFieldsData, poiType);

  const currentSpherical = useAtomValue(state.viewerCurrentSpherical);
  const structureDefaultAssemblyId = useAtomValue(state.structureDefaultAssemblyId);
  const assemblyId = pointOfInterest?.assembly?.id;
  const structureId = pointOfInterest?.assembly?.structure?.id ?? '';
  const setSelectedSavedBlockId = useSetAtom(customBlocksState.selectedSavedBlockId);
  const setShowPointOfInterestPointer = useSetAtom(state.showPointOfInterestPointer);
  const { data: assembliesData, loading: assembliesLoading } = useAllAssembliesForCreatePoiQuery({
    variables: { structureId, templateId: selectedAssemblyTemplateId ?? '' },
    skip: !selectedAssemblyTemplateId,
  });

  const generalFieldsData = useGeneralFields({
    assignee: 'None',
    dueDate: undefined,
    status: PoiStatus.Inreview,
    visibility: VisibilityEnum.Public,
    name: template?.name ?? humanizeString((poiType || '').toString(), '_'),
    teamIds: [],
    workpackNumber: 'N/A',
    comment: '',
    accessibility: undefined,
  });

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const [handlePoiCreate] = useCreatePointOfInterestMutation({
    refetchQueries: [
      GetAssemblyPointOfInterestsDocument,
      GetOneAssemblyForBlistersDocument,
      PointOfInterestsCountByStatusDocument,
      DefectsAreaForCustomBlocksDocument,
      SavedBlockPoIsDocument,
    ],
  });
  const [updatePointOfInterestMutation] = useUpdatePointOfInterestMutation();

  const { inputAdormantHandlerMap, cancelMeasuring } = useMeasurementTool();

  const showBlisterFields = [PIT_TYPE, BLISTER_TYPE].includes(poiType || '');

  const { checkByType } = useIsPoiWorkScope();
  const isWorkScope = checkByType(poiType || '');
  const inOnViewerPage = router.pathname.includes('viewer');
  const isVoi =
    editPointOfInterest.template?.type === PointOfInterestTemplateTypeEnum.VolumeOfInterest;
  const currentCuboid = useAtomValue(cuboidsState.currentCuboid);

  const shouldIncludeCuboids = useMemo(() => {
    return isWorkScope || isVoi;
  }, [isWorkScope, isVoi]);

  const { data: paintRegionsData } = useAllPaintRegionsForFilterAndSearchPanelQuery({
    variables: {
      input: {
        structureId,
        paintRegionGroupIds: selectedPaintRegionGroupId ? [selectedPaintRegionGroupId] : [],
      },
    },
    fetchPolicy: 'cache-first',
  });

  const paintRegionOptions = useMemo(() => {
    return (
      paintRegionsData?.allPaintRegions?.map((paintRegion) => ({
        label: paintRegion.name,
        value: paintRegion.id,
      })) || []
    );
  }, [paintRegionsData?.allPaintRegions]);

  const [selectedPaintRegionId, setSelectedPaintRegionId] = useState('');
  const setSelectedPaintRegionIds = useSetAtom(state.selectedFilteredPaintRegionIds);

  // Lazy query for finding closest paint region
  const [findClosestPaintRegion] = useFindClosestPaintRegionLazyQuery();

  const handlePaintRegionChange = useCallback(
    (paintRegionName: string) => {
      const paintRegion = paintRegionsData?.allPaintRegions?.find(
        (region) => region.name === paintRegionName
      );
      setSelectedPaintRegionId(paintRegion?.id ?? '');
    },
    [paintRegionsData?.allPaintRegions]
  );

  const onPitDropdownFieldChange = useCallback(
    (manualTemplateValue: boolean) => {
      // If the user decides to mark the Blister as PIT
      // update the name of the POI to reflect the change
      generalFieldsData.setState((current) => ({
        ...current,
        // manualTemplateValue being true means the user has checked the checkbox
        // meaning they want to mark the Blister as PIT
        name: manualTemplateValue ? PIT : BLISTER,
      }));
      const newTemplate = allTemplates.find(
        (item) => item.name === (manualTemplateValue ? PIT : BLISTER)
      ) as PointOfInterestTemplateDocument | undefined;

      if (newTemplate) {
        setEditPointOfInterest((current) => ({
          ...current,
          template: newTemplate,
        }));
      }
    },
    [allTemplates, generalFieldsData, setEditPointOfInterest]
  );

  const selectedTemplate = useMemo(() => {
    return allTemplates.find(
      (template) => convertTemplateNameToPoiType(getPoiTemplateKey(template)) === poiType
    );
  }, [allTemplates, poiType]);

  const isMeasurementToolEnabled = useMemo(() => {
    return selectedTemplate?.fields?.some((field) => field.isMeasurementToolEnabled) ?? false;
  }, [selectedTemplate]);

  const locationId = useMemo(
    () => currentSpherical?.id || editPointOfInterest?.poiSphericalId || undefined,
    [currentSpherical?.id, editPointOfInterest?.poiSphericalId]
  );

  const handlePointOfInterest = useCallback(
    async (isInAutoPOIInitFlow: boolean) => {
      const templateFieldData = convertTemplateStateToTemplateFieldData(templateFieldsData.state);
      const generalFields = generalFieldsData.state;
      const type = isInAutoPOIInitFlow
        ? BLISTER_TYPE
        : convertTemplateNameToPoiType(getPoiTemplateKey(template));
      const centerPoint3d = pointOfInterest?.centerPoint3d;
      const cameraLocation = pointOfInterest?.cameraLocation;

      if (!selectedAssemblyTemplateId) {
        showSnackBar('Assembly template is not selected', 'error');
        return;
      }

      const paintRegionIdInput: string | undefined = (() => {
        // This checks if the 'Add to Workscope' flow is active
        // In that, case whatever VOI is being created, originates from
        // the currently selected custom block
        if (
          editPointOfInterest?.customBlockId &&
          selectedTemplate?.type === PointOfInterestTemplateTypeEnum.PaintBlockVolumeOfInterest
        ) {
          return editPointOfInterest.customBlockId;
        }

        return selectedPaintRegionId;
      })();

      if (showBlisterFields && !editPointOfInterest.hasBlisterMeasurement) {
        showSnackBar('Please Generate Measurement before save', 'error');
        return;
      }
      if (
        !structureId ||
        (!isManuallyCreated && !assemblyId) ||
        !type ||
        !templateFieldData ||
        !generalFields ||
        (!poiType && !isInAutoPOIInitFlow)
      ) {
        showSnackBar(
          `Error ${
            isInAutoPOIInitFlow ? 'saving' : 'creating'
          } Point Of Interest. Please contact support if the issue persists.`,
          'error'
        );
        return;
      }
      if (isManuallyCreated && !assemblyId && !structureDefaultAssemblyId) {
        showSnackBar(
          `Error  ${
            isInAutoPOIInitFlow ? 'saving' : 'creating'
          } Point Of Interest. Uncontextualized Assembly not found. Please contact support if the issue persists.`,
          'error'
        );
        return;
      }

      const accessibilityEnum = Object.values(PoiAccessibilityEnum).find(
        (accessibilityValue) =>
          accessibilityValue === upperSnakeCase(generalFieldsData.state.accessibility ?? '')
      );

      let parentPaintRegionId = undefined;

      // If cuboid exists and no manual parent paint region selected, find closest paint region
      if (currentCuboid?.position && structureId) {
        try {
          const { data } = await findClosestPaintRegion({
            variables: {
              input: {
                position: {
                  x: currentCuboid.position[0],
                  y: currentCuboid.position[1],
                  z: currentCuboid.position[2],
                },
                structureId: structureId,
              },
            },
          });

          if (data?.findClosestPaintRegion?.paintRegion?.id) {
            parentPaintRegionId = data.findClosestPaintRegion.paintRegion.id;
          }
        } catch (error) {
          console.error('Error finding closest paint region:', error);
          // Continue with submission even if query fails
        }
      }

      const commonInputFields = {
        ...generalFields,
        name: generalFields?.name ?? '',
        centerPoint3d,
        cameraLocation,
        structureId,
        assemblyId: assemblyId || structureDefaultAssemblyId || '',
        ...(!isInAutoPOIInitFlow && { paintRegionId: paintRegionIdInput }),
        ...(parentPaintRegionId ? { parentPaintRegionId } : {}),
        ...(isInAutoPOIInitFlow ? { type } : { type: poiType }),
        templateFieldData: {
          ...templateFieldData,
          ...(isInAutoPOIInitFlow && { blister_verification: 'Abyss team' }),
        },
        templateId: template?.id,
        locationId,
        blister: localBlisterData,
        dueDate: generalFields?.dueDate?.toISOString(),
        workpackNumber: generalFields.workpackNumber,
        ...(accessibilityEnum ? { accessibility: accessibilityEnum } : {}),
        ...(heightToPointOfInterest ? { viewpointRelativeHeight: heightToPointOfInterest } : {}),
        cuboids: shouldIncludeCuboids
          ? editingCuboids.map((cuboid) => ({
              position: cuboid.position,
              rotation: cuboid.rotation,
              scale: cuboid.scale,
            }))
          : undefined,
        linkedPointOfInterestIds: shouldIncludeCuboids
          ? cuboidLinkedPois?.filter((item) => item?.checked)?.map(({ id }) => id)
          : undefined,
        assemblyTemplateId: selectedAssemblyTemplateId,
      };

      setLoading(true);
      if (editPointOfInterest.pointOfInterest?.centerPoint3d) {
        // Use the value directly as the state setter expects a Maybe<Vector3>
        setNewPointOfInterestCenterPoint3d(editPointOfInterest.pointOfInterest.centerPoint3d);
      }
      let pointOfInterestId: string | undefined;

      try {
        if (isInAutoPOIInitFlow) {
          await updatePointOfInterestMutation({
            variables: {
              input: {
                pointOfInterestId: editPointOfInterest.pointOfInterest?.id ?? '',
                ...commonInputFields,
                isUninitialized: true,
                lastModifiedFor: 'Initialize Auto POI',
                heightType: BlisterHeightType.BlisterTool,
                isManuallyOverridden: true,
              },
            },
            refetchQueries: [
              AllPointOfInterestsForDataLoaderDocument,
              SelectedPointOfInterestDocument,
            ],
          });
          pointOfInterestId = editPointOfInterest.pointOfInterest?.id;
        } else {
          const response = await handlePoiCreate({
            variables: {
              input: {
                ...commonInputFields,
                lastModifiedFor: 'Initialization',
                lineMeasurement: isMeasurementToolEnabled
                  ? propsToPoiLineMeasurement(measurementsMap)
                  : undefined,
              },
            },
          });

          const newPoi = response.data?.createPointOfInterest;
          if (newPoi && !isManuallyCreated) setAllPOI((previous) => [...previous, newPoi]);
          pointOfInterestId = newPoi?.id;

          // update poiCountsByType for the table in POI tab only
          if (pointOfInterestId && editPointOfInterest.isManuallyCreated) {
            setPoiCounts((currentCounts) => {
              const updatedCounts = new Map(
                currentCounts.map((count) => [count.type, count.count])
              );

              updatedCounts.set(poiType, (updatedCounts.get(poiType) || 0) + 1);
              updatedCounts.set('All', (updatedCounts.get('All') || 0) + 1);

              return Array.from(updatedCounts, ([type, count]) => ({ type, count }));
            });
          }
        }
        cancelMeasuring();
        if (isWorkScope) {
          setSelectedSavedBlockId(pointOfInterestId);
          // Refetch saved blocks to include the newly created workscope
          // This ensures the filtering works with complete backend data
          refetchSavedBlocks({
            variables: {
              input: {
                structureId: structureId || '',
                type: 'WORK_SCOPE',
              },
            },
          }).then(({ data }) => {
            if (data?.pointOfInterests) {
              setAllSavedBlocks(data.pointOfInterests);
              setSelectedPaintRegionIds([]);
            }
          });
        } else {
          setSelectedPointOfInterestId(pointOfInterestId);
        }
        setPaintDiameter('0');
        setManualBlisterHeightExists(false);
        let message = '';

        if (isAutoGenerated) {
          message = 'The POI has been saved successfully. The height type is set to Blister Tool.';
        } else {
          message = `${isWorkScope ? 'Work Scope' : 'Point of interest'} created successfully`;
        }

        showSnackBar(message, 'success');

        const sceneOptions: SceneOptions = {
          areSphericalsVisible: false,
          ...(currentSpherical
            ? {
                // only hide point cloud and pois in spherical
                showPointCloudInSpherical: false,
                arePoisVisible: false,
              }
            : {}),
        };

        const prevSceneOptions = setSceneOptions(sceneOptions);

        setIsAddingWorkscope(false);
        stopAddingCuboid();
        setIsCuboidVisibilityBoxEnabled(false);
        setShowPointOfInterestPointer(true);
        onClose();

        // add a delay to ensure the point of interest pointer is visible
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Take the screenshot with the pointer visible
        handleSavePoiScreenshot({
          poiId: pointOfInterestId,
          location: 'attachments',
          refetchPoi: true,
          autoCapture: true,
        });

        // Add a small delay before resetting scene options to ensure the canvas is updated
        // We do not wait for the screenshot to be saved as it is not critical
        await new Promise((resolve) => setTimeout(resolve, 500));
        setSceneOptions(prevSceneOptions);

        if (selectedAssembly) {
          await getAssemblyRecommendation({
            variables: {
              assemblyId: selectedAssembly,
            },
          }).then((assemblyRecommendation) => {
            const governingPOIId =
              assemblyRecommendation.data?.assembly?.recommendation?.governingValue?.pointOfInterest
                ?.id;
            if (governingPOIId) {
              setNominatedBlister((current) => ({
                ...current,
                [selectedAssembly]: {
                  pointOfInterest: {
                    id: governingPOIId,
                  },
                },
              }));
            }
          });
        }
      } catch {
        showSnackBar(
          `Could not ${isAutoGenerated ? 'save' : 'create'} ${
            isWorkScope ? 'Work Scope' : 'POI'
          }. Please try again`,
          'error'
        );
      } finally {
        setLoading(false);
      }
    },
    [
      locationId,
      templateFieldsData.state,
      generalFieldsData.state,
      template,
      pointOfInterest?.centerPoint3d,
      pointOfInterest?.cameraLocation,
      currentSpherical,
      selectedAssemblyTemplateId,
      showBlisterFields,
      editPointOfInterest.hasBlisterMeasurement,
      editPointOfInterest.pointOfInterest?.centerPoint3d,
      editPointOfInterest.pointOfInterest?.id,
      editPointOfInterest.customBlockId,
      editPointOfInterest.isManuallyCreated,
      structureId,
      isManuallyCreated,
      assemblyId,
      poiType,
      structureDefaultAssemblyId,
      currentCuboid?.position,
      localBlisterData,
      heightToPointOfInterest,
      shouldIncludeCuboids,
      editingCuboids,
      cuboidLinkedPois,
      showSnackBar,
      selectedTemplate?.type,
      selectedPaintRegionId,
      findClosestPaintRegion,
      setNewPointOfInterestCenterPoint3d,
      cancelMeasuring,
      isWorkScope,
      setPaintDiameter,
      setManualBlisterHeightExists,
      isAutoGenerated,
      setSceneOptions,
      setIsAddingWorkscope,
      stopAddingCuboid,
      setIsCuboidVisibilityBoxEnabled,
      setShowPointOfInterestPointer,
      onClose,
      handleSavePoiScreenshot,
      selectedAssembly,
      updatePointOfInterestMutation,
      handlePoiCreate,
      isMeasurementToolEnabled,
      measurementsMap,
      setAllPOI,
      setPoiCounts,
      setSelectedSavedBlockId,
      refetchSavedBlocks,
      setAllSavedBlocks,
      setSelectedPaintRegionIds,
      setSelectedPointOfInterestId,
      getAssemblyRecommendation,
      setNominatedBlister,
    ]
  );

  const { data: defectsData } = useFetchWorkscopeDefects({
    // This executes when a POI is of type VOI or PAINT BLOCK VOI (workscope)
    skip: !isWorkScope,
  });

  useEffect(() => {
    if (!defectsData?.dynamicBlockCalculation) return;
    const defects: Record<string, number> = transformObjectKeys(
      defectsData?.dynamicBlockCalculation,
      (key) => key.toLowerCase()
    );
    const fields = template?.fields ?? [];

    Object.entries(defects).forEach((defect) => {
      const key = defect?.[0];
      const value = defect?.[1];
      const found = fields.find((field) => {
        const lowerCaseKey = field.key?.toLowerCase();
        if (!lowerCaseKey) return false;
        return lowerCaseKey === key;
      });

      if (found && isNumeric(value)) {
        const final: number = (() => {
          if (unitSystem === UnitSystemEnum.Metric) return value;
          return convert(value, 'square meter').to('square feet');
        })();
        templateFieldsData.handleChange(String(found.key), final.toFixed(2));
      }
    });
  }, [defectsData?.dynamicBlockCalculation, template?.fields, templateFieldsData, unitSystem]);

  const onCancel = useCallback(() => {
    cancelMeasuring();
    onClose();
    setManualBlisterHeightExists(false);
    stopAddingCuboid();
  }, [cancelMeasuring, onClose, setManualBlisterHeightExists, stopAddingCuboid]);

  const goBackToVoiEditing = () => {
    setEditPointOfInterest((prev) => ({
      ...prev,
      formState: 'Resizing-VOI',
    }));
    startEditingCurrentCuboid();
  };

  const poiTypeOptions = useMemo(() => {
    const excludedPoiTypes = new Set(['Blister', 'Pit', 'Recommendation']);
    return allPoiTemplates
      .filter(({ name }) => !excludedPoiTypes.has(name))
      .map(({ name }) => name);
  }, [allPoiTemplates]);

  const assemblies = useMemo(
    () => assembliesData?.allAssemblies ?? [],
    [assembliesData?.allAssemblies]
  );

  const handleAssemblyChange = useCallback(
    (assemblyName: string) => {
      const assembly = assemblies.find(({ tagName }) => tagName === assemblyName);
      setAssemblyId(assembly?.id ?? '');
    },
    [assemblies, setAssemblyId]
  );

  const uncertainField = useUncertainField({ dataHook: templateFieldsData });

  const buttonLabel = useMemo(() => {
    if (isWorkScope) {
      return ADD_SCOPE_LABEL;
    }
    if (isAutoGenerated) {
      return SAVE_POI_LABEL;
    }
    return ADD_POI_LABEL;
  }, [isWorkScope, isAutoGenerated]);

  return (
    <Grid container sx={{ height: '100%' }}>
      {localBlisterData && (
        <Grid size={{ xs: 7 }}>
          <Suspense>
            <BlisterMeasurementLazy
              poiToAdd={pointOfInterest}
              blisterData={blisterFieldsData}
              uncertainField={uncertainField}
              onMeasure={onMeasure}
              pointSelectMessage={pointSelectMessage}
              onSelectPoint={onSelectPoint}
              setLocalBlisterData={setLocalBlisterData}
              clearBlisterData={clearBlisterData}
              measureOnLoad={editPointOfInterest.formState === 'Create'}
              handleChangeControlledField={handleChangeControlledField}
            />
          </Suspense>
        </Grid>
      )}
      <Grid size={{ xs: localBlisterData ? 5 : 12 }}>
        <StyledForm
          onSubmit={async (event) => {
            event.preventDefault();
            await handlePointOfInterest(!!isAutoGenerated);
          }}
          style={{ borderLeft: '1px solid #ddd' }}
        >
          <Stack
            sx={{ height: `calc(100vh / var(--zoom) - ${navbarHeight}px)`, overflowY: 'auto' }}
          >
            <Box flex={1} mb="8rem">
              <Stack direction="column" sx={{ px: 2 }}>
                <PoiFormHeading variant="h5">Summary</PoiFormHeading>

                <Box sx={fieldsContainer} data-testid="poi-form-container">
                  <PoiFormSubHeading variant="h5">
                    {isWorkScope ? WORK_SCOPE_LABEL : 'POI'} specifications
                  </PoiFormSubHeading>
                  {editPointOfInterest.isManuallyCreated && (
                    <>
                      <DropDownField
                        id="poi-type"
                        data-testid="poi-type-selector"
                        value={currentTemplate}
                        mode="Edit"
                        title="POI Type"
                        options={poiTypeOptions}
                        onFieldValueChange={changePoiTemplate}
                      />
                      <DropDownField
                        id="assemblies"
                        data-testid="assemblies"
                        value={selectedAssemblyId}
                        mode="Edit"
                        title={`Select Assembly ${assembliesLoading ? '(loading ... )' : ''}`}
                        optionsLimit={assemblies.length}
                        options={assemblies.map(({ tagName }) => tagName)}
                        onFieldValueChange={handleAssemblyChange}
                        disabled={assembliesLoading}
                      />
                      <DropDownField
                        id="paint-region"
                        data-testid="paint-region-selector"
                        value={
                          paintRegionOptions.find((opt) => opt.value === selectedPaintRegionId)
                            ?.label ?? ''
                        }
                        mode="Edit"
                        title="Paint Block"
                        options={paintRegionOptions.map((opt) => opt.label)}
                        onFieldValueChange={handlePaintRegionChange}
                      />
                    </>
                  )}
                  {showBlisterFields && (
                    <BlisterFormFields
                      blisterData={blisterFieldsData}
                      handleManualHeightChange={handleManualHeightChange}
                      handleMeasureIconClick={handleMeasureIconClick}
                      onFieldChange={onPitDropdownFieldChange}
                      uncertainField={uncertainField}
                    />
                  )}
                  <TemplateFieldsForm
                    template={template}
                    dataHook={templateFieldsData}
                    formState={editPointOfInterest?.formState}
                    inputAdormantHandlerMap={inputAdormantHandlerMap}
                    handleChangeControlledField={handleChangeControlledField}
                    excludedFieldKeys={isVoi ? (editPointOfInterest.voiDefectFieldKeys ?? []) : []}
                  />
                </Box>
              </Stack>

              <Divider sx={{ mt: 4, color: '#B8CADD' }} />

              <Stack direction="column" sx={{ px: 2 }}>
                <Box sx={fieldsContainer}>
                  <PoiFormSubHeading variant="h5" mt={2}>
                    General Information
                  </PoiFormSubHeading>
                  {shouldIncludeCuboids && <LinkedPoiEditor />}
                  <GeneralFieldsForm
                    teamOptions={teamOptions}
                    teamsLoading={teamsLoading}
                    dataHook={generalFieldsData}
                    currentPoiType={isWorkScope ? WORK_SCOPE_LABEL : undefined}
                    paintRegionOptions={paintRegionOptions}
                    selectedPaintRegionId={selectedPaintRegionId}
                    handlePaintRegionChange={handlePaintRegionChange}
                  />
                </Box>
              </Stack>
            </Box>

            <SaveCancelFormButtons
              // If a VOI is being edited/created AND the user is on viewer
              // show a back button instead of cancel
              cancelButtonLabel={isVoi && inOnViewerPage ? 'Back' : 'Cancel'}
              buttonLabel={buttonLabel}
              loadingLabel={isAutoGenerated ? SAVING_LABEL : CREATING_LABEL}
              onCancel={isVoi && inOnViewerPage ? goBackToVoiEditing : onCancel}
              loading={loading}
              buttonIcon={<AddCircleIcon />}
              sx={saveCancelButtonCommonStyles.sx}
              saveButtonStyles={saveCancelButtonCommonStyles.saveButtonStyles}
              cancelButtonStyles={saveCancelButtonCommonStyles.cancelButtonStyles}
              saveButtonDisabled={isCuboidEditorEnabled}
            />
          </Stack>
        </StyledForm>
      </Grid>
    </Grid>
  );
};
